import { checkTestHost, getSearchParam, loadScript } from "@/utils";
import { isShowNews1 } from "@/utils/isShowNews1";
import { miQsWeatherDomains } from "@/constants/domain.config";
import {
  getCurrentAdScenario,
  runAdScenarioSanityChecks,
  WWW_TRAN_TAB_AD_CONFIG_HOST,
} from "./useAdScenario";

const SCRIPT_URL_GPT = "https://securepubads.g.doubleclick.net/tag/js/gpt.js"; // adx
const SCRIPT_URL_YANDEX = "https://yandex.ru/ads/system/context.js"; // yandex
const adsbygoogleScript = (client: string) =>
  `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;

const PUB_CLIENT_WG_NEWS = "ca-pub-2826423230659304";
const PUB_CLIENT_DEFAULT = "ca-pub-8823204853280729"; // 0729 开了自动底部锚定
const PUB_CLIENT_TRAN = "ca-pub-7826482262281669";
const TRAN_AD_GESTURE_UNLOCK_EVENT = "tran-ad-gesture-unlocked";

const TRAN_AD_GESTURE_EVENTS: (keyof WindowEventMap)[] = ["pointerdown", "touchstart", "click", "wheel", "keydown"];
let hasLoadedAdScripts = false;
let hasBoundTranAdGestureGate = false;
let hasUnlockedTranAdGestureGate = false;
let hasRunAdScenarioChecks = false;

const emitTranAdGestureUnlocked = () => {
  window.dispatchEvent(new Event(TRAN_AD_GESTURE_UNLOCK_EVENT));
};

export const isTranAdGestureUnlocked = () => hasUnlockedTranAdGestureGate;

export const subscribeTranAdGestureUnlock = (listener: () => void) => {
  window.addEventListener(TRAN_AD_GESTURE_UNLOCK_EVENT, listener);
  return () => {
    window.removeEventListener(TRAN_AD_GESTURE_UNLOCK_EVENT, listener);
  };
};

/**
 * 真实 tr-tab 场景需要用户手势后才挂载广告组件，避免脚本未加载时先渲染空占位。
 */
export const useTranAdGestureUnlocked = (shouldGestureGateAds: boolean) => {
  const [isUnlocked, setIsUnlocked] = useState(() => !shouldGestureGateAds || isTranAdGestureUnlocked());

  useEffect(() => {
    if (!shouldGestureGateAds) {
      setIsUnlocked(true);
      return;
    }

    if (isTranAdGestureUnlocked()) {
      setIsUnlocked(true);
      return;
    }

    setIsUnlocked(false);
    return subscribeTranAdGestureUnlock(() => {
      setIsUnlocked(true);
    });
  }, [shouldGestureGateAds]);

  return isUnlocked;
};

const getMilsAndVlsHosts = () => [...(domainMap["mils"] ?? []), ...(domainMap["v-ls"] ?? [])];

const getAdsPubClient9304Hosts = () => [
  ...miwgDomain1,
  ...(domainMap["wg"] ?? []),
  ...(domainMap["miwg"] ?? []),
  ...wgDomains3,
];

const resolveAdsPubClient = (hostName: string) => {
  const isWgDomain = wgDomains1.includes(hostName);
  const use9304Client = isShowNews1().validMain || isWgDomain || getAdsPubClient9304Hosts().includes(hostName);
  return use9304Client ? PUB_CLIENT_WG_NEWS : PUB_CLIENT_DEFAULT;
};

const getScripts = (
  hostName: string,
  {
    isTranTabHost,
    isTranTabChannelScenario,
  }: {
    isTranTabHost: boolean;
    isTranTabChannelScenario: boolean;
  },
) => {
  let baseScripts = [SCRIPT_URL_GPT, SCRIPT_URL_YANDEX];

  const isMilsOrVlsHost = getMilsAndVlsHosts().includes(hostName);
  if (!isMilsOrVlsHost) {
    const adsPubClient = resolveAdsPubClient(hostName);
    baseScripts.splice(1, 0, adsbygoogleScript(adsPubClient)); // ads
  }

  const useMiQsWeather = miQsWeatherDomains.includes(hostName);
  if (useMiQsWeather) {
    baseScripts = [SCRIPT_URL_GPT];
  }

  if (isTranTabHost) {
    baseScripts = [adsbygoogleScript(PUB_CLIENT_TRAN)]; // ads
  }

  if (isTranTabChannelScenario) {
    baseScripts = [SCRIPT_URL_GPT]; // adx
  }

  return baseScripts;
};

const shouldLoadGoogleAdScripts = (hostName: string, isTranTabAdScenario: boolean) =>
  domainLinks.includes(hostName) ||
  checkTestHost(domainLinks) ||
  isShowNews1().validMain ||
  isTranTabAdScenario ||
  miQsWeatherDomains.includes(hostName);

const shouldLoadScriptAsync = (hostName: string) => {
  const syncLoadHosts = [...wgDomains1, ...miwgDomain1, ...domainMap["mils"]];
  return !(syncLoadHosts.includes(hostName) || isShowNews1().validMain);
};

const loadScripts = (scriptsToLoad: string[], hostName: string) => {
  if (hasLoadedAdScripts || !scriptsToLoad.length) {
    return;
  }

  hasLoadedAdScripts = true;
  const loadWithAsync = shouldLoadScriptAsync(hostName);
  for (const scriptUrl of scriptsToLoad) {
    loadScript(
      scriptUrl,
      () => {
        console.log("script loaded:", scriptUrl);
      },
      loadWithAsync,
    );
  }
};

const bindTranAdGestureGate = (onUnlock: () => void) => {
  if (hasUnlockedTranAdGestureGate) {
    onUnlock();
    return;
  }

  if (hasBoundTranAdGestureGate) {
    return;
  }

  hasBoundTranAdGestureGate = true;

  const handler = () => {
    if (hasUnlockedTranAdGestureGate) {
      return;
    }
    hasUnlockedTranAdGestureGate = true;
    TRAN_AD_GESTURE_EVENTS.forEach(eventName => {
      window.removeEventListener(eventName, handler);
    });
    emitTranAdGestureUnlocked();
    onUnlock();
  };

  TRAN_AD_GESTURE_EVENTS.forEach(eventName => {
    window.addEventListener(eventName, handler, { passive: true });
  });
};

export const useGoogleAdScript = () => {
  const { hostName, isTranTabAdScenario, isTranTabHost, isTranTabChannelScenario, shouldGestureGateAds } =
    getCurrentAdScenario();

  if (!hasRunAdScenarioChecks) {
    hasRunAdScenarioChecks = true;
    const valid = runAdScenarioSanityChecks();
    if (!valid) {
      console.warn("广告场景自检失败，请检查 tran 分流规则。");
    }
  }

  if (!shouldLoadGoogleAdScripts(hostName, isTranTabAdScenario)) {
    return;
  }

  const scriptsToLoad = getScripts(hostName, {
    isTranTabHost,
    isTranTabChannelScenario,
  });
  if (shouldGestureGateAds) {
    bindTranAdGestureGate(() => loadScripts(scriptsToLoad, hostName));
    return;
  }

  loadScripts(scriptsToLoad, hostName);
};

const resolveAd1DomainKey = (effectiveAdHost: string) => {
  if (domainLinks.includes(effectiveAdHost)) {
    return effectiveAdHost;
  }

  if (effectiveAdHost === WWW_TRAN_TAB_AD_CONFIG_HOST) {
    return effectiveAdHost;
  }

  const { subdomain } = useSubdomain();
  return subdomain + ".comfortweather.com";
};

export const useGoogleAd1 = (size: string, index?: any) => {
  const { hostName, effectiveAdHost } = getCurrentAdScenario();
  const key1 = resolveAd1DomainKey(effectiveAdHost || hostName);
  const advertList1 = adDomainMap?.[key1]?.[size];
  if (isEmptyType(advertList1)) {
    return;
  }
  return (advertList1 || [])[index];
};

export const useGoogleAd2 = (size: string, index?: any) => {
  const lan = getSearchParam("lan") || "en";
  const con = getSearchParam("con");
  const ch = getSearchParam("channel") || "v_ls_disnews";
  const lan_con = con ? `${lan}_${con.toLowerCase()}` : "";
  const lanKey = lan_con ? lan_con : lan;
  const advertList2 = fileChLanSize?.[ch]?.[lanKey]?.[size];
  return (advertList2 || [])[index];
};
